'use client'

/**
 * Explorateur des projets soutenus — Vitrine, À PARTIR DE 5 PROJETS
 * (src/lib/entreprises/explorateur-projets.ts : seuils, filtres, synthèse).
 *
 * Demande de Guillaume (01/10/2026) : « je ne veux pas quelque chose qui
 * défile à l'infini avec les projets ». Au lieu d'une section par projet :
 *  — la carte au-dessus (un clic sur un point sélectionne le projet) ;
 *  — dès 10 projets, la synthèse par famille, chaque famille filtrant la liste ;
 *  — à gauche, la liste : recherche (nom, commune), région, méthode, année ;
 *  — à droite, le détail COMPLET d'un seul projet (la section de la Vitrine,
 *    rendue par le serveur), « Projet 3 sur 68 », précédent et suivant.
 *
 * Le contenu de TOUS les projets est rendu par le serveur (référencement,
 * ancres #projet-<slug>) ; ce composant ne fait que choisir lequel est
 * visible (`hidden` sur les autres : leurs images, en `loading="lazy"`, ne
 * se chargent qu'une fois le projet affiché).
 *
 * Sur téléphone (< 768 px) : la liste d'abord ; choisir un projet ouvre son
 * détail en plein écran (dialogue), « Retour à la liste » ou le bouton retour
 * du téléphone le referment.
 *
 * Accessibilité : liste en `listbox` / `option` (`aria-selected`), flèches,
 * Début et Fin pour parcourir, Entrée pour ouvrir ; changement de projet
 * annoncé par une région `aria-live` discrète.
 */
import dynamic from 'next/dynamic'
import { createPortal } from 'react-dom'
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import {
  ancreProjet,
  compteur,
  COULEUR_FAMILLE,
  filtreActif,
  filtrerProjets,
  FILTRES_VIDES,
  mosaique,
  optionsFiltres,
  projetParDefaut,
  slugDepuisAncre,
  syntheseFamilles,
  voisins,
  type FamilleExplorateur,
  type FiltresExplorateur,
  type ProjetExplorateur,
} from '@/lib/entreprises/explorateur-projets'
import { Visuel } from './image-publique'

const CarteExplorateur = dynamic(() => import('./carte-explorateur'), {
  ssr: false,
  loading: () => <div className="h-[280px] w-full animate-pulse rounded-2xl bg-sage/60 md:h-[400px]" aria-hidden />,
})

/* ═══════ Panneaux (un par projet, rendus par le serveur) ═══════ */

const Selection = createContext<string | null>(null)

/** Enveloppe d'un projet : visible seulement quand il est sélectionné. */
export function PanneauProjet({ slug, children }: { slug: string; children: ReactNode }) {
  const actif = useContext(Selection)
  return (
    <div data-panneau={slug} hidden={actif !== slug}>
      {children}
    </div>
  )
}

/* ═══════ Outils ═══════ */

const nb = (n: number) => n.toLocaleString('fr-FR')
const TELEPHONE = '(max-width: 767px)'

function useTelephone(): boolean {
  const [tel, setTel] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(TELEPHONE)
    const maj = () => setTel(mq.matches)
    maj()
    mq.addEventListener('change', maj)
    return () => mq.removeEventListener('change', maj)
  }, [])
  return tel
}

const mouvementReduit = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Remplace l'ancre sans recharger ni ajouter d'entrée d'historique. */
function ecrireAncre(slug: string, pousser = false) {
  const url = `${window.location.pathname}${window.location.search}#${ancreProjet(slug)}`
  if (pousser) window.history.pushState({ ...(window.history.state || {}), explorateur: 'detail' }, '', url)
  else window.history.replaceState(window.history.state, '', url)
}

function Fleche({ sens }: { sens: 'gauche' | 'droite' }) {
  return (
    <svg aria-hidden viewBox="0 0 20 20" className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
      <path d={sens === 'gauche' ? 'M12 5l-5 5 5 5' : 'M8 5l5 5-5 5'} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/** Pictogramme d'une famille, quand aucune photo ne la représente. */
function Picto({ famille }: { famille: FamilleExplorateur }) {
  const d: Record<FamilleExplorateur, string> = {
    foret: 'M20 52 L32 20 L44 52 Z M38 52 L52 14 L66 52 Z M52 58 V52 M32 58 V52',
    gestion: 'M24 52 L36 22 L48 52 Z M44 52 L56 18 L68 52 Z M36 58 V52 M56 58 V52',
    agriculture: 'M44 58 V20 M44 26 C36 26 34 20 34 16 M44 26 C52 26 54 20 54 16 M44 36 C36 36 34 30 34 26 M44 36 C52 36 54 30 54 26 M44 46 C36 46 34 40 34 36 M44 46 C52 46 54 40 54 36',
    verger: 'M44 58 V40 M44 40 C30 40 24 32 26 24 C28 16 38 14 44 20 C50 14 60 16 62 24 C64 32 58 40 44 40 Z',
    haies: 'M14 52 C14 40 26 36 30 44 C32 34 46 34 46 44 C50 34 64 36 62 48 C70 42 76 48 74 52 Z',
    batiment: 'M22 56 V32 L44 18 L66 32 V56 Z M36 56 V42 H52 V56',
  }
  return (
    <svg viewBox="0 0 88 72" className="h-16 w-20 text-white/85" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d[famille]} />
    </svg>
  )
}

const BOUTON_NAV =
  'inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 text-[14px] font-semibold text-gray-800 transition-colors hover:border-forest/40 hover:text-forest disabled:pointer-events-none disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest'

const CHAMP =
  'h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-[14px] text-gray-900 focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20'

/* ═══════ Explorateur ═══════ */

export default function ExplorateurProjets({
  items,
  couleur,
  carte,
  entete,
  children,
}: {
  items: ProjetExplorateur[]
  /** Couleur d'accent de l'entreprise. */
  couleur: string
  /** Bloc « carte » coché par l'entreprise. */
  carte: boolean
  /** Titre et chapeau de la section (rendus par le serveur), à gauche de la carte sur grand écran. */
  entete: ReactNode
  /** Un `PanneauProjet` par projet. */
  children: ReactNode
}) {
  const id = useId()
  const telephone = useTelephone()
  const [selection, setSelection] = useState<string | null>(() => projetParDefaut(items))
  const [filtres, setFiltres] = useState<FiltresExplorateur>(FILTRES_VIDES)
  const [ouvert, setOuvert] = useState(false)
  const [annonce, setAnnonce] = useState('')
  const pousse = useRef(false)
  const liste = useRef<HTMLUListElement>(null)
  const detail = useRef<HTMLDivElement>(null)
  const retour = useRef<HTMLButtonElement>(null)

  const parSlug = useMemo(() => new Map(items.map((p) => [p.slug, p])), [items])
  const filtresActifs = filtreActif(filtres)
  const filtres_ = useMemo(() => filtrerProjets(items, filtres), [items, filtres])
  const visibles = useMemo(() => new Set(filtres_.map((p) => p.slug)), [filtres_])
  const options = useMemo(() => optionsFiltres(items), [items])
  const synthese = useMemo(() => syntheseFamilles(items), [items])
  const vignettes = useMemo(() => mosaique(items), [items])
  const v = selection ? voisins(items, filtres_, selection) : null
  const courant = selection ? parSlug.get(selection) ?? null : null
  const points = useMemo(() => items.filter((p) => p.coords), [items])
  const avecCarte = carte && points.length > 0

  /** Amène le haut du détail à l'écran (grand écran) s'il en est sorti. */
  const montrerDetail = useCallback((toujours = false) => {
    const el = detail.current
    if (!el || window.matchMedia(TELEPHONE).matches) return
    const haut = el.getBoundingClientRect().top
    if (toujours ? haut > window.innerHeight * 0.55 || haut < 72 : haut < 72) {
      window.scrollTo({ top: window.scrollY + haut - 96, behavior: mouvementReduit() ? 'auto' : 'smooth' })
    }
  }, [])

  const choisir = useCallback((slug: string, o: { ouvrir?: boolean; source?: 'liste' | 'carte' | 'vignette' | 'navigation' | 'clavier' } = {}) => {
    const p = parSlug.get(slug)
    if (!p) return
    setSelection(slug)
    const tel = window.matchMedia(TELEPHONE).matches
    const ouvrir = tel && o.ouvrir
    if (ouvrir && !ouvert) {
      ecrireAncre(slug, true)
      pousse.current = true
      setOuvert(true)
    } else {
      ecrireAncre(slug)
    }
    const pos = voisins(items, filtrerProjets(items, filtres), slug)
    setAnnonce(`Projet affiché : ${p.titre}, ${pos.position} sur ${pos.total}.`)
    if (tel && (ouvert || ouvrir) && detail.current) detail.current.scrollTop = 0
    if (!tel) requestAnimationFrame(() => montrerDetail(o.source === 'carte' || o.source === 'vignette'))
    // La ligne choisie reste visible dans la liste (défilement interne)
    requestAnimationFrame(() => {
      const ligne = liste.current?.querySelector<HTMLElement>(`[data-slug="${CSS.escape(slug)}"]`)
      ligne?.scrollIntoView({ block: 'nearest' })
    })
  }, [parSlug, items, filtres, ouvert, montrerDetail])

  const fermer = useCallback(() => {
    if (pousse.current) {
      // Le retour d'historique referme le détail (écouteur popstate ci-dessous)
      window.history.back()
      return
    }
    setOuvert(false)
  }, [])

  // ── Arrivée avec une ancre #projet-<slug>, et liens vers une ancre ──
  useEffect(() => {
    const lire = (arrivee: boolean) => {
      const s = slugDepuisAncre(window.location.hash, items)
      if (!s) return
      setSelection(s)
      const tel = window.matchMedia(TELEPHONE).matches
      if (tel) setOuvert(true)
      requestAnimationFrame(() => {
        if (!tel) {
          const el = document.getElementById(ancreProjet(s))
          el?.scrollIntoView({ block: 'start', behavior: arrivee || mouvementReduit() ? 'auto' : 'smooth' })
        }
        liste.current?.querySelector<HTMLElement>(`[data-slug="${CSS.escape(s)}"]`)?.scrollIntoView({ block: 'nearest' })
      })
    }
    lire(true)
    const surAncre = () => lire(false)
    window.addEventListener('hashchange', surAncre)
    return () => window.removeEventListener('hashchange', surAncre)
  }, [items])

  // ── Filtre qui écarte le projet affiché : le premier projet retenu prend sa place ──
  useEffect(() => {
    if (!filtresActifs || !selection || visibles.has(selection) || !filtres_.length) return
    const premier = filtres_[0]!.slug
    setSelection(premier)
    ecrireAncre(premier)
    setAnnonce(`Projet affiché : ${filtres_[0]!.titre}, 1 sur ${filtres_.length}.`)
  }, [filtresActifs, selection, visibles, filtres_])

  // ── Bouton retour du téléphone : referme le détail ouvert depuis la liste ──
  useEffect(() => {
    const surRetour = () => {
      if (pousse.current) {
        pousse.current = false
        setOuvert(false)
      }
    }
    window.addEventListener('popstate', surRetour)
    return () => window.removeEventListener('popstate', surRetour)
  }, [])

  // ── Détail plein écran : page figée dessous, focus dedans, Échap pour sortir ──
  const plein = telephone && ouvert
  const etaitPlein = useRef(false)
  useEffect(() => {
    if (plein) {
      const avant = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      etaitPlein.current = true
      retour.current?.focus()
      return () => { document.body.style.overflow = avant }
    }
    if (etaitPlein.current) {
      etaitPlein.current = false
      // Retour à la liste : le focus revient sur la ligne du projet
      liste.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus()
    }
  }, [plein])

  const surClavierDialogue = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!plein) return
    if (e.key === 'Escape') { e.preventDefault(); fermer(); return }
    if (e.key !== 'Tab' || !detail.current) return
    const f = [...detail.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((x) => x.offsetParent !== null)
    if (!f.length) return
    const premier = f[0]!
    const dernier = f[f.length - 1]!
    if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus() }
    else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus() }
  }

  // ── Liste au clavier : la sélection suit le focus ──
  const surClavierListe = (e: KeyboardEvent<HTMLUListElement>) => {
    if (!filtres_.length) return
    const i = filtres_.findIndex((p) => p.slug === selection)
    let cible: number | null = null
    if (e.key === 'ArrowDown') cible = Math.min(i + 1, filtres_.length - 1)
    else if (e.key === 'ArrowUp') cible = Math.max(i - 1, 0)
    else if (e.key === 'Home') cible = 0
    else if (e.key === 'End') cible = filtres_.length - 1
    else if ((e.key === 'Enter' || e.key === ' ') && selection) {
      e.preventDefault()
      choisir(selection, { ouvrir: true, source: 'clavier' })
      return
    }
    if (cible === null) return
    e.preventDefault()
    const s = filtres_[cible]!.slug
    choisir(s, { source: 'clavier' })
    requestAnimationFrame(() => liste.current?.querySelector<HTMLElement>(`[data-slug="${CSS.escape(s)}"]`)?.focus())
  }

  const majFiltre = <K extends keyof FiltresExplorateur>(cle: K, valeur: FiltresExplorateur[K]) => setFiltres((f) => ({ ...f, [cle]: valeur }))
  const selectionDansListe = selection ? visibles.has(selection) : false
  const focusable = selectionDansListe ? selection : filtres_[0]?.slug ?? null

  const navigation = (bas = false) =>
    v && (
      <nav aria-label={bas ? 'Projet précédent ou suivant' : 'Parcourir les projets'} className={bas ? 'mt-12 grid gap-3 border-t border-gray-200 pt-6 sm:grid-cols-2' : 'flex items-center justify-between gap-3'}>
        {bas ? (
          <>
            {v.precedent ? (
              <button type="button" onClick={() => choisir(v.precedent!, { source: 'navigation' })} className="group flex min-h-[64px] items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left hover:border-forest/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest">
                <Fleche sens="gauche" />
                <span className="min-w-0">
                  <span className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500">Précédent</span>
                  <span className="block truncate font-heading text-[15px] font-bold text-gray-900 group-hover:text-forest">{parSlug.get(v.precedent)?.titre}</span>
                </span>
              </button>
            ) : <span className="hidden sm:block" />}
            {v.suivant && (
              <button type="button" onClick={() => choisir(v.suivant!, { source: 'navigation' })} className="group flex min-h-[64px] items-center justify-end gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-right hover:border-forest/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest">
                <span className="min-w-0">
                  <span className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500">Suivant</span>
                  <span className="block truncate font-heading text-[15px] font-bold text-gray-900 group-hover:text-forest">{parSlug.get(v.suivant)?.titre}</span>
                </span>
                <Fleche sens="droite" />
              </button>
            )}
          </>
        ) : (
          <>
            <p className="text-[14px] font-semibold text-gray-600">
              Projet <span className="tabular-nums text-gray-900">{nb(v.position)}</span> sur <span className="tabular-nums text-gray-900">{nb(v.total)}</span>
            </p>
            <div className="flex gap-2">
              <button type="button" className={BOUTON_NAV} disabled={!v.precedent} onClick={() => v.precedent && choisir(v.precedent, { source: 'navigation' })}>
                <Fleche sens="gauche" />
                <span className="hidden sm:inline">Précédent</span>
                <span className="sr-only sm:hidden">Projet précédent</span>
              </button>
              <button type="button" className={BOUTON_NAV} disabled={!v.suivant} onClick={() => v.suivant && choisir(v.suivant, { source: 'navigation' })}>
                <span className="hidden sm:inline">Suivant</span>
                <span className="sr-only sm:hidden">Projet suivant</span>
                <Fleche sens="droite" />
              </button>
            </div>
          </>
        )}
      </nav>
    )

  const detailProjet = (
        <div
          ref={detail}
          onKeyDown={surClavierDialogue}
          role={plein ? 'dialog' : undefined}
          aria-modal={plein ? true : undefined}
          aria-label={plein && courant ? courant.titre : undefined}
          className={`${
            ouvert
              ? 'fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-cream md:static md:z-auto md:min-w-0 md:overflow-visible md:bg-transparent'
              : 'hidden min-w-0 md:block'
          } ${avecCarte ? 'md:col-start-2 md:row-start-2' : 'md:col-start-2 md:row-start-1'}`}
        >
          <div className="sticky top-0 z-10 border-b border-gray-200 bg-cream/95 px-4 py-2 backdrop-blur md:hidden">
            <div className="flex items-center justify-between gap-2">
              <button ref={retour} type="button" onClick={fermer} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl pr-2 font-heading text-[15px] font-semibold text-forest focus-visible:outline focus-visible:outline-2 focus-visible:outline-forest">
                <Fleche sens="gauche" />
                Retour à la liste
              </button>
              {v && (
                <div className="flex items-center gap-1">
                  <span className="px-1 text-[13px] font-semibold tabular-nums text-gray-600">{nb(v.position)} sur {nb(v.total)}</span>
                  <button type="button" className={`${BOUTON_NAV} px-2.5`} disabled={!v.precedent} onClick={() => v.precedent && choisir(v.precedent, { source: 'navigation' })}>
                    <Fleche sens="gauche" /><span className="sr-only">Projet précédent</span>
                  </button>
                  <button type="button" className={`${BOUTON_NAV} px-2.5`} disabled={!v.suivant} onClick={() => v.suivant && choisir(v.suivant, { source: 'navigation' })}>
                    <Fleche sens="droite" /><span className="sr-only">Projet suivant</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="px-6 pb-12 pt-6 md:p-0">
            <div className="mb-6 hidden md:block">{navigation()}</div>
            {children}
            {navigation(true)}
          </div>
        </div>
  )

  return (
    <Selection.Provider value={selection}>
      <p className="sr-only" aria-live="polite" aria-atomic="true">{annonce}</p>

      <div className="max-w-3xl">{entete}</div>

      {/* ── Synthèse par famille (10 projets et plus) : cartes illustrées, chacune filtre la liste ── */}
      {synthese.length > 0 && (
        <ul aria-label="Filtrer par famille de projets" className={`mt-8 grid grid-cols-2 gap-3 md:gap-4 ${synthese.length >= 4 ? 'lg:grid-cols-4' : synthese.length === 3 ? 'md:grid-cols-3' : ''}`}>
          {synthese.map((s) => {
            const valeur = `famille:${s.famille}`
            const actif = filtres.methode === valeur
            return (
              <li key={s.famille}>
                <button
                  type="button"
                  aria-pressed={actif}
                  onClick={() => majFiltre('methode', actif ? null : valeur)}
                  className={`group relative isolate flex h-[148px] w-full flex-col justify-end overflow-hidden rounded-2xl p-4 text-left text-white transition-shadow focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-forest md:h-[188px] md:p-5 ${actif ? 'ring-4 ring-amber ring-offset-2 ring-offset-cream' : ''}`}
                >
                  {s.photo ? (
                    <span className="absolute inset-0 -z-10 transition-transform duration-500 group-hover:scale-[1.04]">
                      <Visuel src={s.photo.url} alt="" sizes="(min-width: 1024px) 25vw, 50vw" />
                    </span>
                  ) : (
                    <span className="gradient-forest absolute inset-0 -z-10 flex items-start justify-end p-3"><Picto famille={s.famille} /></span>
                  )}
                  <span className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0F2F26]/90 via-[#0F2F26]/45 to-[#0F2F26]/5" aria-hidden />
                  <span className="flex items-center gap-2">
                    <span className="hidden h-2.5 w-2.5 flex-shrink-0 rounded-full ring-2 ring-white/80 md:inline-block" style={{ backgroundColor: s.couleur }} aria-hidden />
                    <span className="font-heading text-[16px] font-bold leading-tight md:text-[21px]">{s.libelle}</span>
                  </span>
                  <span className="mt-1 block text-[12.5px] text-white/85 md:text-[13.5px]">{nb(s.tonnes)} tCO₂ attendues</span>
                  <span className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold ${actif ? 'bg-amber text-gray-900' : 'bg-white/90 text-gray-800 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'}`}>
                    {actif ? 'Filtre actif' : 'Filtrer'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {/* ── Mosaïque : les projets en images, chaque vignette ouvre le projet ── */}
      {vignettes.length > 0 && (
        <div className="mt-8 md:mt-10">
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-forest">Les projets en images</p>
          <ul className="-mx-6 mt-3 flex snap-x snap-mandatory scroll-px-6 gap-3 overflow-x-auto px-6 pb-2 md:mx-0 md:grid md:grid-cols-4 md:overflow-visible md:px-0 md:pb-0 lg:grid-cols-6">
            {vignettes.map((p) => (
              <li key={p.slug} className="w-[42%] flex-shrink-0 snap-start sm:w-[30%] md:w-auto">
                <button
                  type="button"
                  onClick={() => choisir(p.slug, { ouvrir: true, source: 'vignette' })}
                  className="group relative block aspect-[4/3] w-full overflow-hidden rounded-xl bg-sage text-left focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-forest"
                >
                  <span className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.05]">
                    <Visuel src={p.photo!.url} alt="" sizes="(min-width: 1024px) 16vw, (min-width: 768px) 25vw, 42vw" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" aria-hidden />
                  <span className="absolute inset-x-0 bottom-0 p-3">
                    <span className="block font-heading text-[13.5px] font-bold leading-snug text-white">{p.titre}</span>
                    <span className="mt-0.5 block text-[11.5px] text-white/80">{p.methode}{p.tonnes != null ? ` · ${nb(p.tonnes)} t` : ''}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Grand écran : liste à gauche, carte puis détail à droite. Téléphone : carte, liste, détail en plein écran. */}
      <div className="mt-10 grid gap-8 md:mt-12 md:grid-cols-[260px_minmax(0,1fr)] md:gap-x-8 md:gap-y-8 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-x-10">
        {avecCarte && (
          <div className="md:col-start-2 md:row-start-1">
            <CarteExplorateur points={points} selection={selection} visibles={visibles} onSelect={(s) => choisir(s, { ouvrir: true, source: 'carte' })} hauteur="h-[300px] md:h-[360px] lg:h-[440px]" />
          </div>
        )}
        {/* ── Liste ── */}
        <div className="md:sticky md:top-24 md:col-start-1 md:row-span-2 md:row-start-1 md:flex md:max-h-[calc(100vh-7.5rem)] md:flex-col md:self-start">
          <div className="space-y-2.5">
            <label htmlFor={`${id}-q`} className="sr-only">Rechercher un projet (nom, commune)</label>
            <input
              id={`${id}-q`}
              type="search"
              value={filtres.recherche}
              onChange={(e) => majFiltre('recherche', e.target.value)}
              placeholder="Rechercher : nom, commune"
              autoComplete="off"
              className={CHAMP}
            />
            <label htmlFor={`${id}-region`} className="sr-only">Région</label>
            <select id={`${id}-region`} value={filtres.region ?? ''} onChange={(e) => majFiltre('region', e.target.value || null)} className={CHAMP}>
              <option value="">Toutes les régions</option>
              {options.regions.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle} ({o.nombre})</option>)}
            </select>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2.5">
              <label htmlFor={`${id}-methode`} className="sr-only">Méthode</label>
              <select id={`${id}-methode`} value={filtres.methode ?? ''} onChange={(e) => majFiltre('methode', e.target.value || null)} className={CHAMP}>
                <option value="">Méthode</option>
                {options.familles.length > 1 && (
                  <optgroup label="Familles">
                    {options.familles.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle} ({o.nombre})</option>)}
                  </optgroup>
                )}
                <optgroup label="Méthodes">
                  {options.methodes.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle} ({o.nombre})</option>)}
                </optgroup>
              </select>
              <label htmlFor={`${id}-annee`} className="sr-only">Année de soutien</label>
              <select id={`${id}-annee`} value={filtres.annee ?? ''} onChange={(e) => majFiltre('annee', e.target.value ? Number(e.target.value) : null)} className={`${CHAMP} w-[104px]`}>
                <option value="">Année</option>
                {options.annees.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle} ({o.nombre})</option>)}
              </select>
            </div>
          </div>

          <div className="mt-3 flex min-h-[28px] items-center justify-between gap-3 text-[13px]">
            <p className="font-semibold text-gray-700" aria-live="polite">
              {filtresActifs ? compteur(filtres_.length, items.length) : `${nb(items.length)} projets`}
            </p>
            {filtresActifs && (
              <button type="button" onClick={() => setFiltres(FILTRES_VIDES)} className="min-h-[32px] font-semibold text-forest underline-offset-4 hover:underline">
                Tout afficher
              </button>
            )}
          </div>

          <ul
            ref={liste}
            role="listbox"
            aria-label="Projets soutenus"
            onKeyDown={surClavierListe}
            className="mt-2 divide-y divide-gray-200/80 border-y border-gray-200/80 md:min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-contain md:pr-1"
          >
            {filtres_.map((p) => {
              const actif = p.slug === selection
              return (
                <li
                  key={p.slug}
                  id={`${id}-o-${p.slug}`}
                  data-slug={p.slug}
                  role="option"
                  aria-selected={actif}
                  tabIndex={p.slug === focusable ? 0 : -1}
                  onClick={() => choisir(p.slug, { ouvrir: true, source: 'liste' })}
                  className={`relative flex cursor-pointer items-start gap-3 py-3 pl-4 pr-2 outline-none transition-colors focus-visible:bg-sage/70 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest ${actif ? 'bg-white' : 'hover:bg-white/70'}`}
                >
                  <span className="absolute inset-y-2 left-0 w-1 rounded-full" style={{ backgroundColor: actif ? couleur : 'transparent' }} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className={`block font-heading text-[14.5px] font-bold leading-snug ${actif ? 'text-forest' : 'text-gray-900'}`}>{p.titre}</span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] leading-snug text-gray-600">
                      <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: COULEUR_FAMILLE[p.famille] }} aria-hidden />
                      <span className="truncate">{[p.departement, p.methode].filter(Boolean).join(' · ')}</span>
                    </span>
                  </span>
                  {p.tonnes != null && (
                    <span className="flex-shrink-0 pt-0.5 text-right font-heading text-[13.5px] font-bold tabular-nums text-gray-900">
                      {nb(p.tonnes)}&nbsp;t
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
          {filtres_.length === 0 && (
            <p className="py-6 text-[14px] text-gray-600">
              Aucun projet ne correspond.{' '}
              <button type="button" onClick={() => setFiltres(FILTRES_VIDES)} className="font-semibold text-forest underline underline-offset-4">Tout afficher</button>
            </p>
          )}
        </div>

        {/* ── Détail du projet sélectionné : en plein écran sur téléphone, porté sous <body> pour passer
             au-dessus de l'en-tête fixe du site (le fondu d'arrivée de la page crée un contexte d'empilement) ── */}
        {plein ? createPortal(detailProjet, document.body) : detailProjet}
      </div>
    </Selection.Provider>
  )
}
